// Shared type definitions for the game world.

export interface Vec2 {
  x: number;
  y: number;
}

export interface Wall {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Light {
  x: number;
  y: number;
  r: number;
}

export type GuardTag = 'g' | 'h' | 'i';

export interface ParsedMap {
  MAP: string[];
  MW: number;
  MH: number;
  TILE: number;
  WORLD_W: number;
  WORLD_H: number;
  walls: Wall[];
  lights: Light[];
  guardSpawns: Record<GuardTag, Vec2[]>;
  playerStart: Vec2;
  exitPos: Vec2;
  /** Item pickup positions parsed from `*` markers. */
  collectibleSpawns: Vec2[];
  /** Camera positions + initial facing direction parsed from `^v<>`. */
  cameraSpawns: Array<{ pos: Vec2; baseDir: number }>;
}

export interface Player {
  x: number;
  y: number;
  r: number;
  dir: number;
  vx: number;
  vy: number;
  inBox: boolean;
  carryingBody: Guard | null;
  hp: number;
  /** Walking-animation accumulator (radians-ish). Used by the renderer for
   * bob/arm-swing sin waves. Mutated by player.ts when speed > threshold. */
  walkT: number;
}

export type GuardState = 'patrol' | 'suspicious' | 'investigate' | 'chase';

export interface Guard {
  id: number;
  x: number;
  y: number;
  dir: number;
  speed: number;
  vision: number;
  fov: number;
  state: GuardState;
  alert: number;
  path: Vec2[];
  pi: number;
  target: Vec2 | null;
  lastSeen: Vec2 | null;
  idleT: number;
  ko: boolean;
  dragged: boolean;
  hiddenInBox: boolean;

  // Pathfinding state — cached A* output and replan triggers.
  navPath: Vec2[] | null;
  navIdx: number;
  navTargetTile: { tx: number; ty: number } | null;
  navStuckT: number;

  /** Walking-animation accumulator. Mirrors Player.walkT. */
  walkT: number;

  /** Speed (px/s) actually achieved on the previous physics tick.
   * Used by the renderer to pick idle vs walking pose — the static
   * config `speed` field is a max, not the current velocity. */
  currentSpeed: number;
}

export interface Box {
  x: number;
  y: number;
  body: Guard | null;
}

export interface Decoy {
  x: number;
  y: number;
  vx: number;
  vy: number;
  flightT: number;
  life: number;
  pulseT: number;
  landed: boolean;
}

export interface SoundEvent {
  x: number;
  y: number;
  radius: number;
  life: number;
  maxLife: number;
  color: string; // CSS color string used for the ring stroke
}

/** A security camera (parsed from `^v<>`). Pans symmetrically around
 * `baseDir` and can be temporarily disabled with the hack ability. */
export interface Camera {
  x: number;
  y: number;
  /** Center of the pan range, in radians. */
  baseDir: number;
  /** Pan amplitude (rad). The camera sweeps `baseDir ± panAmplitude`. */
  panAmplitude: number;
  /** Pan rate in rad/s. */
  panSpeed: number;
  /** Internal pan accumulator. */
  panT: number;
  fov: number;
  vision: number;
  /** Seconds remaining of hack disable. 0 = active, > 0 = disabled. */
  hackT: number;
}

/** A pickup item placed on the map (parsed from `*`). The player walks
 * over it to collect — the win condition reads world.collectibles. */
export interface Collectible {
  x: number;
  y: number;
  picked: boolean;
  /** Animation accumulator for the pickup pulse / fade-out. */
  pickupT: number;
}

/** What the level requires the player to do before the extraction pad
 * activates. Encoded as a tagged union so the win-check is exhaustive. */
export type Objective =
  | { kind: 'extract' }
  | { kind: 'collect-and-extract' }
  | { kind: 'eliminate-and-extract' };

/** A short-lived visual effect (dust, spark, trail). Pure render state. */
export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: number; // RGB hex
  size: number; // initial radius in px
  drag: number; // velocity damping per second (e.g. 4 = e^-4 by 1s)
  shape: 'circle' | 'spark';
}

export type AbilityName = 'box' | 'knock' | 'decoy' | 'takedown' | 'hack';
export type Cooldowns = Record<AbilityName, number>;

export const STATE = {
  PLAY: 0,
  WIN: 1,
  LOSE: 2,
} as const;
export type GameState = (typeof STATE)[keyof typeof STATE];

/** Per-run statistics, used by the scoring system. */
export interface Stats {
  /** Set to true the first time any guard transitions into the `chase` state. */
  detected: boolean;
  /** Cumulative count of K.O.'d guards. */
  kos: number;
  /** Cumulative count of bodies stashed in a box. */
  bodiesHidden: number;
  /** Cumulative count of decoy throws. */
  decoysUsed: number;
  /** Cumulative count of wall knocks. */
  knocksUsed: number;
}

export interface World extends ParsedMap {
  player: Player;
  guards: Guard[];
  activeBox: Box | null;
  decoys: Decoy[];
  sounds: SoundEvent[];
  particles: Particle[];
  cooldowns: Cooldowns;
  state: GameState;
  time: number;
  /** Reference time (s) below which the run is considered "fast" for ranking. */
  targetTime: number;
  stats: Stats;
  /** Identifier of the LevelDef this world was built from — used to namespace
   * the per-level "best" record in localStorage. */
  levelId: string;
  collectibles: Collectible[];
  cameras: Camera[];
  objective: Objective;
}
